import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Check } from 'lucide-react';
import brand from '../../config/brand';
import { useIsDemo } from '../../hooks/useIsDemo';

// Gradient de la popup selon la marque active
const BRAND_GRADIENT = brand.key === 'dna'
  ? 'from-[#1E4D38] to-[#2D634A]'
  : 'from-[#34B0D8] to-[#2A9BC1]';

/**
 * Composant pour gérer l'installation PWA
 * Affiche une bannière incitant à installer l'app
 */
const PWAInstallPrompt = ({ isAdmin = false }) => {
  const isDemo = useIsDemo();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Vérifier si déjà installé
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Vérifier si déjà refusé récemment (24h)
    const dismissedAt = localStorage.getItem('pwa-dismissed-at');
    if (dismissedAt) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
        return;
      }
    }

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Afficher après 5 secondes de navigation
      setTimeout(() => setShowPrompt(true), 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Détecter l'installation
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-dismissed-at', Date.now().toString());
  };

  // Ne pas afficher si installé, refusé, pas de prompt disponible, ou en mode démo
  if (isDemo || isInstalled || dismissed || !showPrompt || !deferredPrompt) {
    return null;
  }

  const appName = isAdmin ? `${brand.name} Admin` : `${brand.name} Client`;
  const bgColor = BRAND_GRADIENT;
  const description = isAdmin
    ? `Installez l'app Admin pour un accès rapide au CRM`
    : `Installez l'app pour un accès rapide à votre espace`;

  return (
    <div className="fixed bottom-20 lg:bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slideUp">
      <div className={`bg-gradient-to-r ${bgColor} rounded-2xl shadow-2xl p-4 text-white`}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 bg-white/20 p-2.5 rounded-xl">
            <Smartphone size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base mb-1">
              Installer {appName}
            </h3>
            <p className="text-sm text-white/90 mb-3">
              {description}
            </p>
            <div className="flex space-x-2">
              <button
                onClick={handleInstall}
                className="flex-1 flex items-center justify-center space-x-2 bg-white text-gray-900 px-4 py-2 rounded-xl font-semibold text-sm hover:bg-gray-100 transition-colors"
              >
                <Download size={16} />
                <span>Installer</span>
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook pour enregistrer le Service Worker approprié
 */
export const usePWARegistration = (isAdmin = false) => {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const swFile = isAdmin ? '/sw-admin.js' : '/sw.js';
    const scope = isAdmin ? '/admin' : '/';

    const setup = async () => {
      try {
        // Nettoyage anti-bug upload : sur l'admin, un ancien Service Worker
        // parasite (sw-client/courtier/assisteur, enregistrés au scope '/')
        // pouvait prendre le contrôle de la page et intercepter les POST,
        // cassant l'upload de pièces jointes (« Failed to fetch »). On désinscrit
        // tout SW qui n'est pas sw-admin.js, et on recharge une fois si l'un
        // d'eux contrôlait encore la page (pour s'en libérer proprement).
        if (isAdmin) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          let controllerRemoved = false;
          for (const reg of registrations) {
            const scriptURL = (reg.active || reg.waiting || reg.installing)?.scriptURL || '';
            if (scriptURL && !scriptURL.endsWith('/sw-admin.js')) {
              await reg.unregister();
              const controller = navigator.serviceWorker.controller;
              if (controller && controller.scriptURL === scriptURL) {
                controllerRemoved = true;
              }
            }
          }
          if (controllerRemoved && !sessionStorage.getItem('sw-cleanup-reloaded')) {
            sessionStorage.setItem('sw-cleanup-reloaded', '1');
            window.location.reload();
            return;
          }
        }

        const registration = await navigator.serviceWorker.register(swFile, { scope });
        // Forcer la vérification d'une nouvelle version (évince un SW obsolète).
        registration.update();
      } catch (error) {
        console.error('[PWA] Erreur enregistrement SW:', error);
      }
    };

    setup();
  }, [isAdmin]);
};

/**
 * Composant pour afficher l'état d'installation
 */
export const PWAInstallStatus = () => {
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    setIsInstalled(window.matchMedia('(display-mode: standalone)').matches);
  }, []);

  if (!isInstalled) return null;

  return (
    <div className="inline-flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
      <Check size={12} />
      <span>App installée</span>
    </div>
  );
};

export default PWAInstallPrompt;
